/*
* Ввод дробных чисел (КБЖУ и подобное). Правится одна и та же величина в разных формах,
* поэтому поведение поля тоже должно быть одно: пускаем только цифры и один разделитель,
* принимаем и точку, и запятую.
* */

export const sanitizeDecimalInput = (raw: string): string => {
    let value = raw.replace(/[^\d.,]/g, "");

    // Ввели оба разделителя — оставляем тот, что напечатан первым.
    const commaIdx = value.indexOf(",");
    const dotIdx = value.indexOf(".");

    if (commaIdx !== -1 && dotIdx !== -1) {
        value = commaIdx < dotIdx ? value.replace(/\./g, "") : value.replace(/,/g, "");
    }

    const separatorMatch = value.match(/[.,]/);

    if (separatorMatch) {
        const separator = separatorMatch[0];
        const parts = value.split(/[.,]/);
        value = parts[0] + separator + parts.slice(1).join("");
    }

    return value;
};

// Незавершённый ввод («12,») — это ещё не число: отдаём null, чтобы не сохранить 12
// в момент, когда человек набирает 12,5.
export const parseDecimalInput = (value: string): number | null => {
    if (value === "" || value === "." || value === ",") return null;
    if (value.endsWith(".") || value.endsWith(",")) return null;

    const parsed = parseFloat(value.replace(",", "."));

    return isNaN(parsed) ? null : parseFloat(parsed.toFixed(2));
};

export const formatDecimalForInput = (value: number | null): string =>
    value === null ? "" : String(value).replace(".", ",");

/*
* Пара «значение для отправки + строка для показа». Строку держим отдельно, потому что
* промежуточные состояния ввода (пустая строка, «12,») числом не выражаются.
* */
export const createDecimalInputHandler =
    (setValue: (value: number | null) => void, setValueToShow: (value: string) => void) =>
    (raw: string) => {
        const sanitized = sanitizeDecimalInput(raw);

        setValueToShow(sanitized);
        setValue(parseDecimalInput(sanitized));
    };

/*
* То же для денежного поля: сумма уходит в минимальных единицах валюты (`toSend`), поэтому
* значение и строка расходятся сильнее, чем у обычного дробного.
*
* ⚠️ Не через `currencyFormatter.toFloat`/`toStringFloat`: первый вырезает всё, кроме цифр и
* точки, поэтому «10,50» превращался у него в 1050, а второй делал то же самое прямо при
* наборе — запятая исчезала из поля под пальцами, и ввести дробную сумму ею было нельзя.
*
* Незавершённый ввод («10,») значение не трогает: до следующего нажатия сумма остаётся
* прежней. Обнуляем только на пустом поле — это уже осмысленный ноль, а не полпути.
* */
export const createMoneyInputHandler =
    (
        toSend: (value: number) => number,
        setValue: (value: number) => void,
        setValueToShow: (value: string) => void
    ) =>
    (raw: string) => {
        const sanitized = sanitizeDecimalInput(raw);

        setValueToShow(sanitized);

        const parsed = parseDecimalInput(sanitized);

        if (parsed !== null) {
            setValue(toSend(parsed));
        } else if (sanitized === "") {
            setValue(0);
        }
    };
